import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Trophy, ChevronRight } from 'lucide-react'
import { useRecentFixtures, useFavouriteTeams } from '../hooks/useSports'
import { cn } from '../lib/utils'

// Compact horizontally-scrollable match cards on top of the news
// page. Mirrors the Google Search "sports widget" pattern: short
// team codes, final score, live/final pill. Each card links to the
// match detail page. The user's favourite teams' matches surface
// first; everything else follows in kickoff-descending order.
//
// Hidden when there are no recent or live matches — the section
// shouldn't take vertical space on a quiet day.
export default function RecentMatchesWidget() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const isArabic = locale === 'ar'

  const favTeams = useFavouriteTeams()
  const { fixtures, loading } = useRecentFixtures({
    favouriteTeamIds: favTeams.teamIds,
    limit: 12,
  })

  if (loading) return null
  if (!fixtures.length) return null

  return (
    <div className="-mx-1">
      <div className="flex items-center gap-2 mb-1.5 px-2">
        <Trophy className="w-3.5 h-3.5 text-app-primary-500" />
        <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground" dir="auto">
          {t('news.recentMatches')}
        </p>
        <RouterLink
          to="/sports"
          className="ms-auto text-[11px] text-app-primary-500 hover:underline inline-flex items-center gap-0.5"
          dir="auto"
        >
          {t('news.viewAllMatches')}
          <ChevronRight className="w-3 h-3 rtl:-scale-x-100" />
        </RouterLink>
      </div>
      {/* dir="ltr" on the scroll container is intentional. In RTL the
          flex order flips so fixtures[0] renders at the right edge,
          and the natural scrollLeft start (0) shows the LAST card
          first — visually backwards. Locking just this strip to LTR
          keeps the first match on the left and starts the scroll
          there, which is what "show me my next match first" expects
          regardless of UI language. The cards' own text uses
          dir="auto" so league / team names still render RTL when
          they should. */}
      <div
        className="flex gap-2 overflow-x-auto px-1 pb-1 scrollbar-thin snap-x snap-mandatory"
        dir="ltr"
      >
        {fixtures.map((f) => (
          <MatchPill key={f.id} fixture={f} isArabic={isArabic} t={t} />
        ))}
      </div>
    </div>
  )
}

// Compact relative-time formatter for upcoming match cards. "In 2h"
// / "Tomorrow" / "Sat 19:00". Keeps the card narrow.
function fmtUpcoming(iso, locale) {
  if (!iso) return ''
  const kickoff = new Date(iso)
  const diff = kickoff.getTime() - Date.now()
  const hours = Math.round(diff / 3600_000)
  if (hours < 0) {
    // Already started but still scheduled — render the kickoff time.
    return kickoff.toLocaleTimeString(locale === 'ar' ? 'ar-EG' : 'en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true,
    })
  }
  if (hours < 1) return locale === 'ar' ? 'قريب' : 'Soon'
  if (hours < 12) return locale === 'ar' ? `بعد ${hours}س` : `In ${hours}h`
  // Same calendar day?
  const today = new Date()
  const startOfTomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)
  if (kickoff < startOfTomorrow) {
    return kickoff.toLocaleTimeString(locale === 'ar' ? 'ar-EG' : 'en-US', {
      hour: 'numeric', minute: '2-digit', hour12: true,
    })
  }
  const startOfDayAfter = new Date(startOfTomorrow.getTime() + 24 * 3600 * 1000)
  if (kickoff < startOfDayAfter) return locale === 'ar' ? 'بكرة' : 'Tomorrow'
  return kickoff.toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-US', {
    weekday: 'short', day: 'numeric',
  })
}

// One card in the widget. Status drives the centre layout:
//   - scheduled → kickoff time / "Tomorrow" / "In 5h"
//   - live      → red score + elapsed minute
//   - finished  → score + FT pill
// ~180px wide so 2 cards are visible on a phone at once.
function MatchPill({ fixture, isArabic, t }) {
  const isLive = fixture.status === 'live'
  const isScheduled = fixture.status === 'scheduled'
  // Resolve locale once for the upcoming-time formatter (we're inside
  // useTranslation upstream but this is a child render).
  const locale = isArabic ? 'ar' : 'en'
  const home = fixture.home_team || ''
  const away = fixture.away_team || ''
  // Three-letter-ish code. Football-data's shortName is usually short
  // enough already ("Man City", "Real Madrid"), but for the widget we
  // strip down further: take first word and uppercase to 3-5 chars.
  // Crests carry the visual brand so the code can be small.
  const code = (s) => {
    if (!s) return ''
    const first = s.split(/\s+/)[0]
    return first.slice(0, 4).toUpperCase()
  }
  const leagueName = isArabic && fixture.league?.name_ar
    ? fixture.league.name_ar
    : fixture.league?.name
  return (
    <RouterLink
      to={`/sports/${fixture.api_id}`}
      className="snap-start flex-shrink-0 w-[180px]"
    >
      <div className={cn(
        'rounded-xl border bg-card hover:border-app-primary-500/40 transition-colors p-2.5 h-full',
        // Subtle highlight on scheduled (favourite-team) cards so the
        // user can tell upcoming-of-mine apart from the recents at a
        // glance.
        isScheduled ? 'border-app-primary-500/30' : 'border-border',
      )}>
        <div className="flex items-center justify-between gap-1 mb-2">
          <span
            className="text-[10px] text-foreground font-medium truncate"
            dir="auto"
            title={`${home} vs ${away}`}
          >
            {code(home)} vs {code(away)}
          </span>
          {isLive ? (
            <span className="text-[9px] font-bold text-red-500 uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-red-500/10 animate-pulse">
              {fixture.elapsed_minutes != null ? `${fixture.elapsed_minutes}'` : 'LIVE'}
            </span>
          ) : isScheduled ? (
            <span className="text-[9px] font-bold text-app-primary-500 uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-app-primary-500/10" dir="auto">
              {fmtUpcoming(fixture.kickoff_at, locale)}
            </span>
          ) : (
            <span className="text-[9px] font-bold text-muted-foreground uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-muted">
              {t('sports.match.statusFinished')}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          {fixture.home_logo_url ? (
            <img
              src={fixture.home_logo_url}
              alt=""
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = 'none' }}
              className="w-6 h-6 object-contain flex-shrink-0"
            />
          ) : <span className="w-6 h-6 flex-shrink-0" />}
          {isScheduled ? (
            // Kickoff time in the user's locale + 12h clock. Sits
            // where the score would go on finished/live cards, so
            // "when does this start" is the most prominent piece of
            // info on a scheduled card — matches how Google's
            // sports widget presents upcoming fixtures.
            <span
              className="text-sm font-bold text-foreground font-mono tabular-nums whitespace-nowrap"
              dir="ltr"
            >
              {new Date(fixture.kickoff_at).toLocaleTimeString(
                locale === 'ar' ? 'ar-EG' : 'en-US',
                { hour: 'numeric', minute: '2-digit', hour12: true },
              )}
            </span>
          ) : (
            <span
              className={cn(
                'text-base font-bold font-mono tabular-nums',
                isLive ? 'text-red-500' : 'text-foreground',
              )}
              dir="ltr"
            >
              {fixture.home_score ?? 0}
              <span className="text-muted-foreground mx-1">–</span>
              {fixture.away_score ?? 0}
            </span>
          )}
          {fixture.away_logo_url ? (
            <img
              src={fixture.away_logo_url}
              alt=""
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = 'none' }}
              className="w-6 h-6 object-contain flex-shrink-0"
            />
          ) : <span className="w-6 h-6 flex-shrink-0" />}
        </div>
        {leagueName && (
          <p className="text-[9px] text-muted-foreground truncate mt-1.5 uppercase tracking-wide" dir="auto">
            {leagueName}
          </p>
        )}
      </div>
    </RouterLink>
  )
}
